import { useState } from "react";
import { Check, Sparkles, Crown } from "lucide-react";
import { Link } from "react-router-dom";

import FoundingClaimModal from "./FoundingClaimModal";

const TIERS = [
  {
    key: "free",
    icon: Check,
    eyebrow: "FREE TO START",
    title: "Try BlendSafe free",
    blurb:
      "Run safety checks, browse ingredients, and use core compatibility tools. No card to begin.",
    perks: [
      "Free safety checks to start",
      "Barcode + photo ingredient scan",
      "Instant safety verdict",
      "1,500+ products covered",
      "Built for US consumers",
    ],
    cta: { label: "Run a free check", to: "/check", kind: "link" },
    accent: "#0e6b34", // muted green
    iconBg: "rgba(34,197,94,0.10)",
    bg: "#F4F1EA",                                   // cooler cream
    borderColor: "rgba(14,107,52,0.14)",
    shadow:
      "0 1px 2px rgba(0,0,0,0.03), 0 10px 24px -14px rgba(14,107,52,0.18)",
  },
  {
    key: "founding",
    icon: Crown,
    eyebrow: "FOUNDING MEMBERS",
    title: "Premium intelligence",
    blurb:
      "Memory, continuity, and personalized monitoring — the premium platform after launch.",
    highlight: true,
    perks: [
      "Saved AM/PM routines + history",
      "Personalized skin logic (sensitive, rosacea, pregnancy-safe)",
      "Continuous routine monitoring",
      "Dermatologist-ready PDF export",
      "Extended scanning + advanced analysis",
      "Pro free for 12 months when payments launch",
    ],
    cta: { label: "Claim founding access", kind: "modal" },
    accent: "#a87a32",
    iconBg: "rgba(168,122,50,0.12)",
    bg: "linear-gradient(135deg,#fdf7e9 0%,#f6ecd1 100%)",
    borderColor: "rgba(168,122,50,0.28)",
    shadow:
      "0 1px 2px rgba(0,0,0,0.04), 0 20px 50px -16px rgba(168,122,50,0.22)",
  },
];

/**
 * WhatYouGetSection — concise 2-tier comparison.
 * FREE positions BlendSafe's safety checker as open + honest; FOUNDING /
 * PREMIUM is framed as skincare intelligence (memory, personalization,
 * monitoring) — NOT as paywalled safety. The dedicated
 * `WhatUnlocksAfterLaunchSection` upstream of this block carries the
 * post-launch feature vision so this section stays clean.
 */
export default function WhatYouGetSection() {
  const [claimOpen, setClaimOpen] = useState(false);

  return (
    <section
      data-testid="what-you-get"
      className="relative max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-16"
    >
      <header className="max-w-2xl mb-9 sm:mb-10">
        <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-ink-mute mb-2.5">
          Pricing
        </p>
        <h2
          data-testid="pricing-heading"
          className="text-[28px] sm:text-[36px] md:text-[40px] font-extrabold tracking-[-0.025em] leading-[1.08] text-ink"
        >
          <span style={{ color: "#0e6b34" }}>Free safety checking.</span>{" "}
          <span style={{ color: "#a87a32" }}>Premium</span>{" "}
          skincare intelligence.
        </h2>
        <p className="mt-3 text-[14.5px] sm:text-[15px] text-ink-soft/85 leading-relaxed max-w-xl">
          BlendSafe stays free for safety checks. Premium adds memory,
          continuity, and intelligent monitoring when you&rsquo;re ready.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 max-w-4xl">
        {TIERS.map((tier) => {
          const Icon = tier.icon;
          const isHighlight = !!tier.highlight;
          return (
            <article
              key={tier.key}
              data-testid={`tier-${tier.key}`}
              className={[
                "group relative rounded-2xl border p-6 sm:p-7 flex flex-col transition-all duration-300",
                "hover:-translate-y-1 hover:shadow-floating",
                isHighlight ? "lg:-translate-y-2" : "",
              ].join(" ")}
              style={{
                background: tier.bg,
                borderColor: tier.borderColor,
                boxShadow: tier.shadow,
              }}
            >
              {isHighlight && (
                <span
                  className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.22em] text-[#a87a32]"
                  style={{
                    background: "linear-gradient(135deg,#f4e8d0,#e8d4a8)",
                    border: "1px solid rgba(168,122,50,0.3)",
                  }}
                >
                  <Sparkles className="w-3 h-3" /> Most loved
                </span>
              )}

              <div className="flex items-center gap-2 mb-2.5">
                <span
                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl"
                  style={{ background: tier.iconBg, color: tier.accent }}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <p
                  className="text-[10px] uppercase tracking-[0.24em] font-semibold"
                  style={{ color: tier.accent }}
                >
                  {tier.eyebrow}
                </p>
              </div>

              <h3
                className="text-[19px] sm:text-[21px] leading-[1.18] font-extrabold tracking-[-0.012em] text-ink"
              >
                {tier.title}
              </h3>
              <p className="mt-2 text-[13.5px] text-ink-mute leading-snug">{tier.blurb}</p>

              <ul className="mt-4 space-y-2 flex-1">
                {tier.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2.5 text-[13.5px] text-ink-soft leading-snug">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: tier.accent }} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>

              {tier.cta && tier.cta.kind === "link" && (
                <div className="mt-5">
                  <Link
                    to={tier.cta.to}
                    data-testid={`tier-cta-${tier.key}`}
                    className="inline-flex items-center justify-center w-full h-11 rounded-xl text-[13.5px] font-semibold transition-all hover:scale-[1.01] bg-white border border-surface-line text-ink hover:border-ink/30 hover:shadow-sm"
                  >
                    {tier.cta.label}
                  </Link>
                </div>
              )}
              {tier.cta && tier.cta.kind === "modal" && (
                <div className="mt-5">
                  <button
                    type="button"
                    onClick={() => setClaimOpen(true)}
                    data-testid={`tier-cta-${tier.key}`}
                    className="inline-flex items-center justify-center w-full h-11 rounded-xl text-[13.5px] font-semibold transition-all hover:scale-[1.01] text-cream-50 hover:opacity-95"
                    style={{
                      background:
                        "linear-gradient(135deg,#1f3d2e 0%,#2c5240 50%,#1f3d2e 100%)",
                    }}
                  >
                    {tier.cta.label}
                  </button>
                </div>
              )}
            </article>
          );
        })}
      </div>

      <FoundingClaimModal open={claimOpen} onOpenChange={setClaimOpen} />
    </section>
  );
}
